import { useEffect, useId, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useForm } from "react-hook-form";
import { CheckCircle2, Loader2, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getEmailWidgetButtonBackground } from "@/lib/emailWidgetStyle";
import { ctaBorderRadius } from "@/lib/ctaButtonStyle";
import { getBrandThemeFromColors } from "@/utils/branding/genieBrandTheme";
import { buildHelpGenieAttributionUrl } from "@/lib/helpGenieAttributionLink";
import type { AskByEmailValues, AskByEmailWidgetProps } from "@/types/ask-by-email";

/** The same Genie-branded surface is used by the live embed and owner previews. */
export const AskByEmailWidget = ({ genieName, branding, onClose, preview, onSubmit, urlName }: AskByEmailWidgetProps) => {
  const id = useId();
  const [submission, setSubmission] = useState<AskByEmailValues | null>(null);
  const confirmationRef = useRef<HTMLHeadingElement>(null);
  const {
    register,
    handleSubmit,
    setFocus,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<AskByEmailValues>({ defaultValues: { question: "", email: "" } });

  const theme = getBrandThemeFromColors(branding);
  const embed = branding.embedStyle;
  const buttonBackground = getEmailWidgetButtonBackground(branding);
  const logo = branding.logoUrl || branding.customAvatarUrl || "/helpgenie_logo.png";
  const fieldClass = "block w-full rounded-xl border border-genie-border bg-genie-background px-4 py-3 text-base text-gray-900 placeholder:text-gray-500 transition-shadow focus:bg-white focus:outline-none focus:ring-2 focus:ring-[var(--email-widget-primary)] disabled:opacity-60";

  useEffect(() => {
    if (submission) confirmationRef.current?.focus();
  }, [submission]);

  return (
    <section
      aria-labelledby={`${id}-title`}
      className="relative w-full overflow-hidden rounded-3xl border border-genie-border bg-white font-sans text-gray-900"
      style={{
        boxShadow: embed?.shadowEnabled === false ? "none" : "0 8px 32px rgb(34 43 69 / 8%)",
        "--email-widget-primary": theme.primary,
      } as CSSProperties}
    >
      <div aria-hidden="true" className="h-1 w-full" style={{ background: theme.gradient }} />
      <header className="relative px-6 pb-1 pt-7 text-center">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl border border-genie-border bg-white p-2 shadow-sm">
          <img src={logo} alt="" className="h-full w-full object-contain" />
        </div>
        <div className="min-w-0">
          <p className="break-words text-sm font-medium text-gray-600">{genieName}</p>
          <h1 id={`${id}-title`} className="mt-3 text-2xl font-bold tracking-tight text-[#222B45]">How can I help?</h1>
          {!embed?.widgetHideWelcomeText && (
            <p className="mt-2 text-sm leading-relaxed text-gray-500">Ask a question. Get a reply by email.</p>
          )}
        </div>
        {onClose && <button
          type="button"
          aria-label="Close email widget"
          className="absolute right-2 top-2 flex min-h-11 min-w-11 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-genie-background hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>}
      </header>

      <div className="p-5 sm:p-6">
        <form
          hidden={!!submission}
          noValidate
          onSubmit={handleSubmit(async (values) => {
            clearErrors("root");
            const normalized = { question: values.question.trim(), email: values.email.trim() };
            try {
              if (!preview) await onSubmit(normalized);
              setSubmission(normalized);
            } catch (error) {
              setError("root", {
                message: error instanceof Error ? error.message : "Couldn’t send your question. Please try again.",
              });
            }
          })}
          className="space-y-4"
        >
          <div>
            <label htmlFor={`${id}-question`} className="mb-2 block text-sm font-medium text-gray-700">Your question</label>
            <textarea
              id={`${id}-question`}
              rows={3}
              maxLength={2000}
              disabled={isSubmitting}
              placeholder="What would you like to know?"
              className={`${fieldClass} resize-y`}
              aria-invalid={!!errors.question}
              aria-describedby={errors.question ? `${id}-question-error` : undefined}
              {...register("question", {
                validate: (value) => !!value.trim() || "Add your question.",
                maxLength: { value: 2000, message: "Keep your question under 2,000 characters." },
              })}
            />
            {errors.question && (
              <p id={`${id}-question-error`} role="alert" className="mt-2 text-sm text-genie-error">{errors.question.message}</p>
            )}
          </div>
          <div>
            <label htmlFor={`${id}-email`} className="mb-2 block text-sm font-medium text-gray-700">Your email</label>
            <input
              id={`${id}-email`}
              type="email"
              autoComplete="email"
              maxLength={254}
              disabled={isSubmitting}
              placeholder="you@example.com"
              className={fieldClass}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? `${id}-email-error` : undefined}
              {...register("email", {
                setValueAs: (value: string) => value.trim(),
                required: "Add your email address.",
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address." },
                maxLength: { value: 254, message: "Check the length of your email address." },
              })}
            />
            {errors.email && (
              <p id={`${id}-email-error`} role="alert" className="mt-2 text-sm text-genie-error">{errors.email.message}</p>
            )}
          </div>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="min-h-12 w-full gap-2 whitespace-normal px-4 py-3 font-semibold shadow-sm transition-all hover:opacity-90 hover:shadow-md"
            style={{
              background: buttonBackground,
              color: "#FFFFFF",
              borderRadius: ctaBorderRadius(embed?.buttonStyle),
            }}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {isSubmitting ? "Sending your question…" : "Email me an answer"}
            {!isSubmitting && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          </Button>
          {errors.root && <p role="alert" className="text-sm text-genie-error">{errors.root.message}</p>}
          <p className="text-center text-xs leading-relaxed text-gray-500">{preview ? "Preview only · No emails sent" : "Your answer comes by email. Reply to keep chatting."}</p>
        </form>

        {submission && (
          <div className="space-y-4 py-6 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8" style={{ color: theme.primary }} aria-hidden="true" />
            <h2 ref={confirmationRef} tabIndex={-1} className="text-lg font-semibold text-gray-900 focus:outline-none">
              {preview ? "That’s all it takes." : "Your question is on its way."}
            </h2>
            <p className="break-words text-sm text-gray-600">
              {preview ? `${genieName} would reply to` : "Watch for a reply at"} <strong>{submission.email}</strong>.
            </p>
            <p className="text-xs text-gray-500">
              {preview ? "This is a preview. No email was sent." : "Reply to that email to keep chatting."}
            </p>
            <button
              type="button"
              className="min-h-11 rounded-lg px-4 text-sm underline underline-offset-4"
              onClick={() => {
                setSubmission(null);
                requestAnimationFrame(() => setFocus("question"));
              }}
            >
              Ask another question
            </button>
          </div>
        )}
      </div>

      {!branding.hideAttribution && (
        <footer className="flex items-center justify-center gap-1.5 border-t border-genie-border px-4 py-3 text-xs text-gray-500">
          <img src="/helpgenie_logo.png" alt="" className="h-4 w-4 object-contain" />
          <a
            href={buildHelpGenieAttributionUrl(urlName)}
            target="_blank"
            rel="noopener"
            className="transition-opacity hover:opacity-70"
          >
            Made magical by Help Genie
          </a>
        </footer>
      )}
    </section>
  );
};
